import { genId } from "@smartsheet/lodestar-utils";
import React, { forwardRef, useMemo } from "react";
import { useCombinedThemeMode } from "../../util/theme/useCombinedThemeMode";
import { VisuallyHidden } from "../visuallyhidden";
import { FormLabelChild } from "./styled/FormLabelChild";
import { FormLabelDescription } from "./styled/FormLabelDescription";
import { FormLabelFeedback } from "./styled/FormLabelFeedback";
import { FormLabelIcon } from "./styled/FormLabelIcon";
import { FormLabelLabel, labelVariantProps } from "./styled/FormLabelLabel";
import { FormLabelStyled } from "./styled/FormLabelStyled";
import { FormLabelValidation } from "./styled/FormLabelValidation";
import { descriptionVariantProps } from "./styled/formLabelColorTheme";
import { iconBasedOnValidationState } from "./styled/formLabelIcons";
import { FormLabelChildProps, FormLabelProps } from "./types";

export const FormLabel = forwardRef<HTMLElement, FormLabelProps>(
    (
        {
            children,
            id,
            label: labelText,
            labelProps: providedLabelProps,
            description,
            descriptionProps: providedDescriptionProps,
            validationRootProps,
            hideLabel,
            validationState,
            validationProps: providedValidationProps,
            validationMessage,
            icon,
            clientId,
            "aria-describedby": ariaDescribedBy,
            ...rest
        },
        ref
    ) => {
        const themeMode = useCombinedThemeMode();

        const childId = useMemo(() => id ?? genId("fl-input"), [id]);

        const providedLabelId = providedLabelProps?.id;
        const labelId = useMemo(() => providedLabelId ?? genId("fl-label"), [providedLabelId]);
        const finalLabelProps = {
            htmlFor: childId,
            ...providedLabelProps,
            id: labelId,
            validationState: validationState ?? "info",
        } as const;

        const providedDescriptionId = providedDescriptionProps?.id;
        const descId = useMemo(() => providedDescriptionId ?? genId("fl-desc"), [providedDescriptionId]);
        const finalDescriptionProps = {
            ...providedDescriptionProps,
            id: descId,
        } as const;

        const childProps: FormLabelChildProps = {
            id: childId,
            "aria-labelledby": labelId,
            ...(description && { "aria-describedby": `${descId} ${ariaDescribedBy ?? ""}`.trim() }),
        };

        const label = (
            <FormLabelLabel
                renderAs="label"
                {...finalLabelProps}
                {...labelVariantProps[themeMode]}
                themeMode={themeMode}
            >
                {labelText}
            </FormLabelLabel>
        );

        return (
            <FormLabelStyled data-client-id={clientId} ref={ref as React.ForwardedRef<HTMLDivElement>} {...rest}>
                {hideLabel ? <VisuallyHidden>{label}</VisuallyHidden> : label}
                <FormLabelDescription
                    {...finalDescriptionProps}
                    {...descriptionVariantProps[themeMode]}
                    themeMode={themeMode}
                >
                    {description}
                </FormLabelDescription>
                <FormLabelChild>{children(childProps)}</FormLabelChild>
                <FormLabelFeedback
                    orientation="row"
                    space="xxSmall"
                    aria-live="assertive"
                    role="alert"
                    aria-busy={validationState === "waiting"}
                    {...validationRootProps}
                >
                    {validationMessage && validationState && (
                        <>
                            <FormLabelIcon validationState={validationState || "info"} themeMode={themeMode}>
                                {icon ?? iconBasedOnValidationState(validationState, themeMode)}
                            </FormLabelIcon>
                            <FormLabelValidation
                                validationState={validationState}
                                {...providedValidationProps}
                                {...descriptionVariantProps[themeMode]}
                                themeMode={themeMode}
                            >
                                {validationMessage}
                            </FormLabelValidation>
                        </>
                    )}
                </FormLabelFeedback>
            </FormLabelStyled>
        );
    }
);

FormLabel.displayName = "FormLabel";
